import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BookLesson from './BookLesson';
import './TeacherProfile.css';

const TeacherProfile = () => {
  const { teacherId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [teacher, setTeacher] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [bookedLessons, setBookedLessons] = useState([]);
  const [blockedHours, setBlockedHours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBookModal, setShowBookModal] = useState(false);

  useEffect(() => {
    if (!user || user.user_type !== 'student') {
      navigate('/dashboard');
      return;
    }
    fetchTeacher();
    fetchTeacherSchedule();
    fetchBookedLessons();
    fetchBlockedHours();
  }, [teacherId, user, navigate]);

  const fetchTeacher = async () => {
    try {
      const response = await api.get(`/auth/teachers/`);
      const foundTeacher = response.data.find(t => t.id === parseInt(teacherId));
      if (foundTeacher) {
        setTeacher(foundTeacher);
      } else {
        navigate('/teachers');
      }
    } catch (error) {
      console.error('Ошибка загрузки преподавателя:', error);
      navigate('/teachers');
    } finally {
      setLoading(false);
    }
  };

  const fetchTeacherSchedule = async () => {
    try {
      // Получаем расписание конкретного учителя
      const response = await api.get(`/lessons/schedule/?teacher_id=${teacherId}`);
      console.log('[TeacherProfile] Ответ API расписания:', response.data);
      console.log('[TeacherProfile] Тип данных:', typeof response.data, Array.isArray(response.data));
      
      // Обрабатываем пагинированный ответ
      let scheduleData = [];
      if (response.data.results) {
        // Пагинированный ответ
        scheduleData = response.data.results;
        console.log('[TeacherProfile] Пагинированный ответ, результатов:', scheduleData.length);
      } else if (Array.isArray(response.data)) {
        // Обычный массив
        scheduleData = response.data;
        console.log('[TeacherProfile] Обычный массив, элементов:', scheduleData.length);
      } else {
        console.warn('[TeacherProfile] Неожиданный формат ответа:', response.data);
      }
      
      console.log('[TeacherProfile] Установка расписания:', scheduleData);
      setSchedule(scheduleData);
    } catch (error) {
      console.error('[TeacherProfile] Ошибка загрузки расписания:', error);
      console.error('[TeacherProfile] Детали ошибки:', error.response?.data);
      setSchedule([]);
    }
  };

  const fetchBookedLessons = async () => {
    try {
      // Получаем все уроки и фильтруем уроки этого учителя
      const response = await api.get('/lessons/lessons/');
      const allLessons = response.data.results || response.data || [];
      // Фильтруем уроки этого учителя со статусом scheduled или in_progress
      const booked = allLessons.filter(lesson => 
        lesson.teacher === parseInt(teacherId) && 
        (lesson.status === 'scheduled' || lesson.status === 'in_progress')
      );
      setBookedLessons(booked);
    } catch (error) {
      console.error('Ошибка загрузки забронированных уроков:', error);
      setBookedLessons([]);
    }
  };

  const fetchBlockedHours = async () => {
    try {
      // Пытаемся получить заблокированные часы (может быть недоступно для студентов)
      const response = await api.get(`/lessons/blocked-hours/`, {
        params: {
          teacher: teacherId
        }
      }).catch(() => ({ data: [] }));
      setBlockedHours(response.data.results || response.data || []);
    } catch (error) {
      console.error('Ошибка загрузки заблокированных часов:', error);
      setBlockedHours([]);
    }
  };

  const getNextDateForDay = (dayOfWeek) => {
    const today = new Date();
    const jsDayOfWeek = dayOfWeek === 6 ? 0 : dayOfWeek + 1;
    const currentDay = today.getDay();
    let daysUntilDay = (jsDayOfWeek - currentDay + 7) % 7;
    if (daysUntilDay === 0) daysUntilDay = 7;
    
    const targetDate = new Date(today);
    targetDate.setUTCDate(today.getUTCDate() + daysUntilDay);
    targetDate.setUTCHours(0, 0, 0, 0);
    return targetDate;
  };

  const handleBookLesson = async () => {
    // Обновляем расписание перед открытием модального окна
    await fetchTeacherSchedule();
    console.log('[TeacherProfile] Расписание перед открытием модального окна:', schedule);
    setShowBookModal(true);
  };

  const handleCloseModal = () => {
    setShowBookModal(false);
    // Обновляем расписание после закрытия модального окна (на случай, если были изменения)
    fetchTeacherSchedule();
  };

  const getDayName = (dayOfWeek) => {
    const days = ['Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота', 'Воскресенье'];
    return days[dayOfWeek] || '';
  };

  const getAvailableHours = (dayOfWeek) => {
    // Базовые доступные часы из расписания
    const baseHours = schedule
      .filter(slot => slot.day_of_week === dayOfWeek && slot.is_available)
      .map(slot => slot.hour);
    
    // Вычисляем ближайшую дату для этого дня недели
    const nextDate = getNextDateForDay(dayOfWeek);
    const dateStr = nextDate.toISOString().split('T')[0];
    
    // Исключаем заблокированные часы
    let availableHours = [...baseHours];
    
    // Убираем повторяющиеся блокировки для этого дня недели
    const recurringBlocks = blockedHours.filter(
      block => block.is_recurring && block.day_of_week === dayOfWeek
    );
    const blockedHoursRecurring = recurringBlocks.map(block => block.hour);
    availableHours = availableHours.filter(h => !blockedHoursRecurring.includes(h));
    
    // Убираем одноразовые блокировки на эту дату
    const oneTimeBlocks = blockedHours.filter(
      block => !block.is_recurring && block.date === dateStr
    );
    const blockedHoursOneTime = oneTimeBlocks.map(block => block.hour);
    availableHours = availableHours.filter(h => !blockedHoursOneTime.includes(h));
    
    // Убираем уже забронированные уроки на эту дату
    const bookedOnDate = bookedLessons.filter(lesson => {
      const lessonDate = new Date(lesson.scheduled_at);
      const lessonDateStr = lessonDate.toISOString().split('T')[0];
      return lessonDateStr === dateStr && 
             (lesson.status === 'scheduled' || lesson.status === 'in_progress');
    });
    const bookedHours = bookedOnDate.map(lesson => {
      const lessonDate = new Date(lesson.scheduled_at);
      return lessonDate.getUTCHours();
    });
    availableHours = availableHours.filter(h => !bookedHours.includes(h));
    
    // Сортируем и убираем дубликаты
    return [...new Set(availableHours)].sort((a, b) => a - b);
  };

  if (loading) {
    return (
      <div className="teacher-profile-page">
        <Header />
        <main className="teacher-profile-main">
          <div className="teacher-profile-container">
            <div className="loading-message">Загрузка...</div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!teacher) {
    return null;
  }

  return (
    <div className="teacher-profile-page">
      <Header />
      <main className="teacher-profile-main">
        <div className="teacher-profile-container">
          <button 
            className="btn-back"
            onClick={() => navigate('/teachers')}
          >
            ← Назад к списку
          </button>

          <div className="teacher-profile-header">
            <div className="teacher-profile-avatar">
              {teacher.avatar_url ? (
                <img src={teacher.avatar_url} alt={teacher.username} />
              ) : (
                <div className="teacher-avatar-placeholder">
                  {teacher.first_name?.[0] || teacher.username[0].toUpperCase()}
                </div>
              )}
            </div>
            <div className="teacher-profile-info">
              <h1 className="teacher-profile-name">
                {teacher.first_name || ''} {teacher.last_name || ''}
              </h1>
              <p className="teacher-profile-username">@{teacher.username}</p>
              {teacher.bio && (
                <p className="teacher-profile-bio">{teacher.bio}</p>
              )}
              {teacher.country && (
                <p className="teacher-profile-country">📍 {teacher.country}</p>
              )}
            </div>
          </div>

          <div className="teacher-schedule-section">
            <h2 className="section-title">Расписание преподавателя</h2>
            <div className="schedule-grid">
              {[0, 1, 2, 3, 4, 5, 6].map(dayOfWeek => {
                const hours = getAvailableHours(dayOfWeek);
                return (
                  <div key={dayOfWeek} className="schedule-day">
                    <div className="schedule-day-name">{getDayName(dayOfWeek)}</div>
                    <div className="schedule-hours">
                      {hours.length > 0 ? (
                        hours.map(hour => (
                          <span key={hour} className="schedule-hour">
                            {String(hour).padStart(2, '0')}:00
                          </span>
                        ))
                      ) : (
                        <span className="schedule-no-hours">Нет доступных часов</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="teacher-actions">
            <button 
              className="btn-book-lesson"
              onClick={handleBookLesson}
            >
              Забронировать урок
            </button>
          </div>
        </div>
      </main>
      <Footer />

      {showBookModal && (
        <BookLesson
          teacher={teacher}
          teacherSchedule={schedule}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};

export default TeacherProfile;
